import { z } from 'zod';
import { addDays, compareDateOnly, DateOnlySchema, daysBetween } from '../../domain/date-only.ts';
import { DEFAULT_WORK_WEEK, type Company, type WorkWeek } from '../../domain/entities/company.ts';
import {
  WorkdayExceptionReasonSchema,
  WorkdayKindSchema,
  type WorkdayException,
} from '../../domain/entities/schedule.ts';
import type { WorkCalendar } from '../../domain/work-calendar.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { jobCalendar } from './job-calendar.ts';

/**
 * A shutdown longer than a year is a mistyped year, not a plan. Accepting it
 * would write hundreds of documents before anyone noticed.
 */
const MAX_RANGE_DAYS = 366;

export const SetWorkdayExceptionsInputSchema = z.object({
  fromDate: DateOnlySchema,
  /** Omitted for a single day. Inclusive when given. */
  toDate: DateOnlySchema.optional(),
  kind: WorkdayKindSchema,
  reason: WorkdayExceptionReasonSchema,
  note: z.string().trim().max(500).optional(),
});
export type SetWorkdayExceptionsInput = z.input<typeof SetWorkdayExceptionsInputSchema>;

export const WorkdayExceptionRangeSchema = z.object({
  fromDate: DateOnlySchema,
  toDate: DateOnlySchema.optional(),
});
export type WorkdayExceptionRange = z.input<typeof WorkdayExceptionRangeSchema>;

export interface WorkdayExceptionContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

export class WorkdayExceptionValidationError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(fieldErrors: Record<string, string>) {
    const summary = Object.entries(fieldErrors)
      .map(([field, message]) => `${field}: ${message}`)
      .join('; ');
    super(`That workday exception needs attention — ${summary}`);
    this.name = 'WorkdayExceptionValidationError';
    this.fieldErrors = fieldErrors;
  }
}

function parseOrThrow<T extends z.ZodTypeAny>(schema: T, input: unknown): z.output<T> {
  const parsed = schema.safeParse(input);
  if (parsed.success) return parsed.data;

  const fieldErrors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    fieldErrors[issue.path.join('.') || 'form'] ??= issue.message;
  }
  throw new WorkdayExceptionValidationError(fieldErrors);
}

/** Every date in the inclusive range, validated as a span someone could mean. */
function datesIn(fromDate: string, toDate: string | undefined): string[] {
  const last = toDate ?? fromDate;
  if (compareDateOnly(fromDate, last) > 0) {
    throw new WorkdayExceptionValidationError({
      toDate: 'That range ends before it starts',
    });
  }
  const span = daysBetween(fromDate, last) + 1;
  if (span > MAX_RANGE_DAYS) {
    throw new WorkdayExceptionValidationError({
      toDate: `A range covers at most ${MAX_RANGE_DAYS} days; that one covers ${span}`,
    });
  }
  return Array.from({ length: span }, (_, offset) => addDays(fromDate, offset));
}

function repository(context: WorkdayExceptionContext) {
  return context.data.collection<WorkdayException>(
    { companyId: context.companyId, jobId: context.jobId },
    'workdayExceptions',
  );
}

/**
 * Mark one date, or an inclusive range, as worked or not worked on this job.
 *
 * Writes are keyed by date, so setting a date twice converges on one record
 * rather than leaving two rulings for the schedule to choose between.
 */
export async function setWorkdayExceptions(
  context: WorkdayExceptionContext,
  input: SetWorkdayExceptionsInput,
): Promise<WorkdayException[]> {
  const parsed = parseOrThrow(SetWorkdayExceptionsInputSchema, input);
  const dates = datesIn(parsed.fromDate, parsed.toDate);
  const note = parsed.note ? parsed.note : undefined;
  const now = context.clock.now().toISOString();
  const repo = repository(context);

  const written: WorkdayException[] = [];
  for (const date of dates) {
    const existing = await repo.get(date);
    const exception: WorkdayException = {
      id: date,
      companyId: context.companyId,
      jobId: context.jobId,
      date,
      kind: parsed.kind,
      reason: parsed.reason,
      note,
      // Replacing a ruling keeps its original author: "who closed this day
      // first" is the question the baseline tab asks later.
      createdAt: existing?.createdAt ?? now,
      createdBy: existing?.createdBy ?? context.uid,
      updatedAt: now,
      updatedBy: context.uid,
    };

    if (existing) {
      const { id: _id, ...patch } = exception;
      await repo.update(date, patch);
    } else {
      await repo.create(exception);
    }
    written.push(exception);
  }

  return written;
}

/** Remove the rulings in a range, returning the dates that were actually set. */
export async function clearWorkdayExceptions(
  context: WorkdayExceptionContext,
  range: WorkdayExceptionRange,
): Promise<string[]> {
  const parsed = parseOrThrow(WorkdayExceptionRangeSchema, range);
  const repo = repository(context);

  const removed: string[] = [];
  for (const date of datesIn(parsed.fromDate, parsed.toDate)) {
    if (!(await repo.get(date))) continue;
    await repo.delete(date);
    removed.push(date);
  }
  return removed;
}

export async function listWorkdayExceptions(
  context: WorkdayExceptionContext,
): Promise<WorkdayException[]> {
  const page = await repository(context).list({
    orderBy: [{ field: 'date', direction: 'asc' }],
  });
  return [...page.items];
}

/**
 * The calendar this job's dates are computed against: the company working
 * week, overridden by the job's own exceptions.
 */
export async function loadJobCalendar(context: WorkdayExceptionContext): Promise<WorkCalendar> {
  const [company, exceptions] = await Promise.all([
    context.data
      .collection<Company>({ companyId: context.companyId }, 'companies')
      .get(context.companyId),
    listWorkdayExceptions(context),
  ]);

  // A missing company record must not take the schedule down with it; the
  // standard week is the same default a new company is created with.
  const workWeek: WorkWeek = company?.workWeek ?? DEFAULT_WORK_WEEK;
  return jobCalendar(workWeek, exceptions, context.jobId);
}
